import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Select from '../../components/ui/Select';
import { SongAnalysisService } from '../../services/songAnalysisService';

const AdminPage = () => {
  const { user, userProfile, isSuperUser, getAllUsers, updateUserProfile, deleteUser, adminResetUserPassword, createUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [songCount, setSongCount] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    username: '',
    fullName: '',
    role: 'user'
  });
  const [editUserData, setEditUserData] = useState({
    email: '',
    username: '',
    full_name: '',
    role: 'user',
    is_active: true,
    email_verified: false
  });

  useEffect(() => {
    if (user && userProfile && !isSuperUser()) {
      navigate('/');
      return;
    }
    
    if (user && userProfile) {
      loadUsers();
      loadSongCount();
    }
  }, [user, userProfile, navigate, isSuperUser]);

  const loadUsers = async () => {
    setLoading(true);
    setError(''); // Clear any existing errors
    
    console.log('Loading users...', { currentUserRole: userProfile?.role, isSuperUser: isSuperUser() });
    
    const { data, error } = await getAllUsers();
    
    if (error) {
      console.error('Failed to load users:', error);
      setError(`Failed to load users: ${error?.message || 'Unknown error'}`);
      
      // Add debugging information for troubleshooting
      if (error?.code === 'PGRST301' || error?.code === '42501') {
        setError(`Access denied. Current user role: ${userProfile?.role}. This might be an RLS policy issue.`);
      }
    } else {
      console.log(`Loaded ${data?.length || 0} users successfully`);
      setUsers(data || []);
      
      // Log user details for debugging Mat Satuloff issue
      const matUser = data?.find(u => 
        u?.full_name?.toLowerCase()?.includes('mat') && u?.full_name?.toLowerCase()?.includes('satuloff') ||
        u?.username?.toLowerCase()?.includes('mat') ||
        u?.email?.toLowerCase()?.includes('mat')
      );
      
      if (matUser) {
        console.log('Found Mat Satuloff in user list:', matUser);
      } else {
        console.warn('Mat Satuloff not found in user list. Users loaded:', data?.map(u => ({ username: u?.username, full_name: u?.full_name, email: u?.email })));
      }
    }
    setLoading(false);
  };

  const loadSongCount = async () => {
    try {
      const { data, error } = await SongAnalysisService?.getSongAnalyses();
      if (!error && data) {
        setSongCount(data?.length || 0);
      }
    } catch (err) {
      console.error('Error loading song count:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e?.preventDefault();
    setActionLoading({ create: true });
    setError('');
    setSuccess('');

    // Validate required fields
    if (!newUser?.email?.trim()) {
      setError('Email is required');
      setActionLoading({});
      return;
    }

    if (!newUser?.password?.trim()) {
      setError('Password is required');
      setActionLoading({});
      return;
    }

    if (!newUser?.username?.trim()) {
      setError('Username is required');
      setActionLoading({});
      return;
    }

    // Validate password strength
    if (newUser?.password?.length < 6) {
      setError('Password must be at least 6 characters long');
      setActionLoading({});
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(newUser?.email)) {
      setError('Please enter a valid email address');
      setActionLoading({});
      return;
    }

    try {
      console.log('Creating user with data:', {
        email: newUser?.email,
        username: newUser?.username,
        fullName: newUser?.fullName,
        role: newUser?.role
      });

      const { data, error } = await createUser(
        newUser?.email,
        newUser?.password,
        newUser?.username,
        newUser?.fullName || newUser?.username,
        newUser?.role
      );

      if (error) {
        console.error('User creation failed:', error);
        setError(error?.message || 'Failed to create user');
      } else {
        console.log('User created successfully:', data);
        setSuccess('User created successfully');
        setShowCreateModal(false);
        setNewUser({ email: '', password: '', username: '', fullName: '', role: 'user' });
        
        // Reload users list after a short delay to ensure database consistency
        setTimeout(() => {
          loadUsers();
        }, 1000);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Unexpected error during user creation:', err);
      setError(err?.message || 'Failed to create user');
    } finally {
      setActionLoading({});
    }
  };

  const handleEditUser = (userData) => {
    setEditingUser(userData?.id);
    setEditUserData({
      email: userData?.email || '',
      username: userData?.username || '',
      full_name: userData?.full_name || '',
      role: userData?.role || 'user',
      is_active: userData?.is_active !== false,
      email_verified: userData?.email_verified || false
    });
    setShowEditModal(true);
  };

  const handleUpdateUserSubmit = async (e) => {
    e?.preventDefault();
    if (!editingUser) return;
    
    setActionLoading({ [`update_${editingUser}`]: true });
    setError('');
    setSuccess('');

    try {
      // Handle password reset if new password provided
      if (showPasswordInput && newPassword?.trim()) {
        const { error: passwordError } = await adminResetUserPassword(editingUser, newPassword);
        
        if (passwordError) {
          setError(`Failed to reset password: ${passwordError?.message}`);
          setActionLoading({});
          return;
        }
      }

      // Prepare update data with proper email verification handling
      const updateData = {
        email: editUserData?.email,
        username: editUserData?.username,
        full_name: editUserData?.full_name,
        role: editUserData?.role,
        is_active: editUserData?.is_active,
        email_verified: editUserData?.email_verified,
        // Set email_verified_at to current timestamp if verified, null if unverified
        email_verified_at: editUserData?.email_verified ? new Date()?.toISOString() : null
      };

      // Update user profile data
      const { error } = await updateUserProfile(editingUser, updateData);

      if (error) {
        setError(error?.message || 'Failed to update user');
      } else {
        const successMsg = showPasswordInput && newPassword?.trim() 
          ? 'User updated and password reset successfully' :'User updated successfully';
        setSuccess(successMsg);
        setShowEditModal(false);
        setEditingUser(null);
        setNewPassword('');
        setShowPasswordInput(false);
        setEditUserData({
          email: '',
          username: '',
          full_name: '',
          role: 'user',
          is_active: true,
          email_verified: false
        });
        loadUsers();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to update user');
    } finally {
      setActionLoading({});
    }
  };

  const cancelEdit = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setNewPassword('');
    setShowPasswordInput(false);
    setEditUserData({
      email: '',
      username: '',
      full_name: '',
      role: 'user',
      is_active: true,
      email_verified: false
    });
  };

  const handleUpdateUser = async (userId, updates) => {
    setActionLoading({ [`update_${userId}`]: true });
    setError('');

    try {
      const { error } = await updateUserProfile(userId, updates);

      if (error) {
        setError(error?.message || 'Failed to update user');
      } else {
        setSuccess('User updated successfully');
        setEditingUser(null);
        loadUsers();
      }
    } catch (err) {
      setError('Failed to update user');
    } finally {
      setActionLoading({});
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setActionLoading({ [`delete_${userId}`]: true });
    setError('');

    try {
      const { error } = await deleteUser(userId);

      if (error) {
        setError(error?.message || 'Failed to delete user');
      } else {
        setSuccess('User deleted successfully');
        loadUsers();
      }
    } catch (err) {
      setError('Failed to delete user');
    } finally {
      setActionLoading({});
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="lg" className="text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Header 
        title="Admin Dashboard" 
        user={userProfile}
        onLogout={handleLogout}
        actions={[
          ...(isSuperUser() ? [
            <Button 
              key="database"
              variant="outline" 
              onClick={() => navigate('/admin-dashboard')}
              className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
            >
              Database
            </Button>
          ] : []),
          <Button 
            key="songs"
            variant="outline" 
            onClick={() => navigate('/song-database-list')}
            className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
          >
            Song List
          </Button>
        ]}
      />
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Stats Cards - Add verified/unverified user counts */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">
                      {users?.filter(u => u?.role === 'user')?.length || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Users</dt>
                    <dd className="text-lg font-medium text-white">Regular Users</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">
                      {users?.filter(u => u?.role === 'editor')?.length || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Editors</dt>
                    <dd className="text-lg font-medium text-white">Content Editors</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">
                      {users?.filter(u => u?.role === 'superuser')?.length || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">SuperUsers</dt>
                    <dd className="text-lg font-medium text-white">Administrators</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">
                      {users?.filter(u => u?.email_verified === true)?.length || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Verified</dt>
                    <dd className="text-lg font-medium text-white">Email Verified</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">{songCount}</span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Songs</dt>
                    <dd className="text-lg font-medium text-white">Total Songs</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">{users?.length || 0}</span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Total</dt>
                    <dd className="text-lg font-medium text-white">All Users</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 bg-opacity-90 border border-green-200 rounded-xl p-4">
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Add New User Button - Only for Superusers */}
        {isSuperUser() && (
          <div className="mb-6 flex justify-end">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg px-6 py-2 font-semibold shadow-md hover:shadow-lg transition-shadow"
            >
              Add New User
            </Button>
          </div>
        )}

        {/* Users Table - Add verification status column */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm shadow-lg overflow-hidden rounded-xl">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-white">User Management</h3>
            <p className="mt-1 max-w-2xl text-sm text-blue-100">
              Manage user accounts, roles, permissions, and email verification status.
            </p>
          </div>
          <div className="border-t border-white border-opacity-20">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white divide-opacity-20">
                <thead className="bg-black bg-opacity-20">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider">
                      Email Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-blue-100 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white divide-opacity-20">
                  {users?.map((userData) => (
                    <tr key={userData?.id} className="hover:bg-white hover:bg-opacity-10">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-indigo-600">
                                {userData?.username?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {userData?.full_name || userData?.username}
                            </div>
                            <div className="text-sm text-blue-200">{userData?.email}</div>
                            <div className="text-xs text-blue-300">@{userData?.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userData?.role === 'superuser' ? 'bg-purple-100 text-purple-800'
                            : userData?.role === 'editor' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {userData?.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userData?.is_active 
                            ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {userData?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userData?.email_verified 
                            ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {userData?.email_verified ? 'Verified' : 'Unverified'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-200">
                        {new Date(userData?.created_at)?.toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditUser(userData)}
                            className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                          >
                            Edit
                          </Button>
                          {userData?.id !== user?.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(userData?.id)}
                              disabled={actionLoading?.[`delete_${userData?.id}`]}
                              className="border-red-400 text-red-300 hover:bg-red-400 hover:text-white rounded-lg"
                            >
                              {actionLoading?.[`delete_${userData?.id}`] ? 
                                <LoadingSpinner size="sm" /> : 'Delete'
                              }
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-xl bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 border-white border-opacity-20">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-white mb-4">Create New User</h3>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Username"
                    value={newUser?.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e?.target?.value })}
                    required
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={newUser?.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e?.target?.value })}
                    required
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <Input
                    type="text"
                    placeholder="Full Name"
                    value={newUser?.fullName}
                    onChange={(e) => setNewUser({ ...newUser, fullName: e?.target?.value })}
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={newUser?.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e?.target?.value })}
                    required
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <Select
                    value={newUser?.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e?.target?.value })}
                    options={[
                      { value: 'user', label: 'User' },
                      { value: 'editor', label: 'Editor' },
                      { value: 'superuser', label: 'SuperUser' }
                    ]}
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white"
                  />
                  <div className="flex justify-end space-x-3 mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateModal(false)}
                      className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={actionLoading?.create}
                      className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
                    >
                      {actionLoading?.create ? <LoadingSpinner size="sm" /> : 'Create User'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal - Add email verification toggle */}
        {showEditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-96 shadow-lg rounded-xl bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 border-white border-opacity-20">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-white mb-4">Edit User</h3>
                <form onSubmit={handleUpdateUserSubmit} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Username"
                    value={editUserData?.username}
                    onChange={(e) => setEditUserData({ ...editUserData, username: e?.target?.value })}
                    required
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={editUserData?.email}
                    onChange={(e) => setEditUserData({ ...editUserData, email: e?.target?.value })}
                    required
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <Input
                    type="text"
                    placeholder="Full Name"
                    value={editUserData?.full_name}
                    onChange={(e) => setEditUserData({ ...editUserData, full_name: e?.target?.value })}
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <Select
                    value={editUserData?.role}
                    onChange={(e) => setEditUserData({ ...editUserData, role: e?.target?.value })}
                    options={[
                      { value: 'user', label: 'User' },
                      { value: 'editor', label: 'Editor' },
                      { value: 'superuser', label: 'SuperUser' }
                    ]}
                    className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white"
                  />
                  
                  {/* Password Reset Section */}
                  <div className="border-t border-white border-opacity-20 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-white">Reset Password</label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setShowPasswordInput(!showPasswordInput)}
                        className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                      >
                        {showPasswordInput ? 'Cancel' : 'Change Password'}
                      </Button>
                    </div>
                    {showPasswordInput && (
                      <Input
                        type="password"
                        placeholder="New Password (min 6 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e?.target?.value)}
                        className="rounded-lg bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                      />
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center space-x-2 text-white">
                      <input
                        type="checkbox"
                        checked={editUserData?.is_active}
                        onChange={(e) => setEditUserData({ ...editUserData, is_active: e?.target?.checked })}
                        className="rounded border-white border-opacity-30"
                      />
                      <span className="text-sm">Active User</span>
                    </label>
                    <label className="flex items-center space-x-2 text-white">
                      <input
                        type="checkbox"
                        checked={editUserData?.email_verified}
                        onChange={(e) => setEditUserData({ ...editUserData, email_verified: e?.target?.checked })}
                        className="rounded border-white border-opacity-30"
                      />
                      <span className="text-sm">Email Verified</span>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-3 mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelEdit}
                      className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={actionLoading?.[`update_${editingUser}`]}
                      className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
                    >
                      {actionLoading?.[`update_${editingUser}`] ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;